import * as React from "react";

import {
    Button,
    createStyles,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Theme,
    Typography,
    withStyles,
} from "@material-ui/core";

import * as utils from "../../app/utils";
import { FeedbackRequest } from "../../services/api/FeedbackRequest";
import feedbackService from "../../services/api/FeedbackService";

import { inject, observer } from "mobx-react";
import { IConfigStoreProps } from "../../stores/IConfigStoreProps";
import { IContextStoreProps } from "../../stores/IContextStoreProps";
import { PredefinedStoreName } from "../enums/PredefinedStoreName";
import AppIcon from "../shared/AppIcon";
import { AppIconType } from "../shared/AppIconType";

import i18n from "../../services/localization/i18n";

import {CloseableDialogTitle} from "../shared/CloseableDialog";
import { isTeamsAddonMode } from "../../utils";
import { PredefinedAchievementScenario } from "../../services/api/PredefinedAchievementScenario";
import { AchivementsRequest } from "../../services/api/AchievementsRequest";
import { PredefinedAchievementCheckpoint } from "../../services/api/PredefinedAchievementCheckpoint";
import achievementsService from "../../services/api/AchievementsService";
import { ClientTelemetryEvent } from "../../services/tx/ClientTelemetryEvent";
import telemetryDataService from "../../services/tx/TelemetryDataService";

const styles = (theme: Theme) => createStyles({
    button: {
        borderRadius: 0,
        flexGrow: 1,
        minWidth: theme.spacing(0.5),
        padding: theme.spacing(0.5),
    },
    buttonGroup: {
        display: "flex",
        marginTop: theme.spacing(0.5),
    },
    dialogContent: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    firstButton: {
        borderBottomRightRadius: 0,
        borderTopRightRadius: 0,
        flexGrow: 1,
        minWidth: theme.spacing(0.5),
        padding: theme.spacing(0.5),
    },
    lastButton: {
        borderBottomLeftRadius: 0,
        borderTopLeftRadius: 0,
        flexGrow: 1,
        minWidth: theme.spacing(0.5),
        padding: theme.spacing(0.5),
    },
    leftSmallIcon: {
        fontSize: 20,
        marginRight: theme.spacing(),
    },
    legend: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        paddingRight: theme.spacing(2)
    },
    ratingLegend: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        paddingTop: theme.spacing(1),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    text: {
        minHeight: 96,
        width: "100%",
    },
});

interface IFeedbackProps {

    classes?: {
        button: string;
        buttonGroup: string;
        dialogContent: string;
        firstButton: string;
        lastButton: string;
        leftSmallIcon: string;
        legend: string;
        ratingLegend: string;
        text: string;
    };
    open: boolean;
    onClose: () => void;
}

interface IFeedbackState {
    rating: number;
    text?: string;
}

@inject(PredefinedStoreName.CONTEXT)
@inject(PredefinedStoreName.CONFIG)
@observer
class Feedback extends React.Component<IFeedbackProps & IContextStoreProps & IConfigStoreProps, IFeedbackState> {

    private correlationId = Date.now.toString();

    /**
     * Creates an instance of Feedback.
     * @param {IFeedbackProps} props
     * @memberof Feedback
     */
    constructor(props: IFeedbackProps & IContextStoreProps & IConfigStoreProps) {
        super(props);

        this.state = {
            rating: -1
     ,   };
    }

    public componentDidUpdate() {
        const { resourceId, resourceType } = this.props.contextStore!;
        const { environment, version } = this.props.configStore!;

        if (this.props.open) {
            const scenario = isTeamsAddonMode   ? PredefinedAchievementScenario.TEAMS_ONBOARDING 
                                                : PredefinedAchievementScenario.MEET_GENERAL;

            const achievement: AchivementsRequest  = {
                scenario: scenario,
                checkpoint: PredefinedAchievementCheckpoint.MEET_FEEDBACK_SHOWN,
                resourceId: resourceId!,
                resourceType: resourceType!
            };

            achievementsService.postAsync(achievement);

            const txEvent = {
                correlationId: this.correlationId,
                environment: environment,
                name: "spa_meetApp_feedbackShown",
                sentryEventId: "N/A",
                silent: false,
                source: "meetApp",
                success: true,
                timeToComplete: 0,
                version: version,
            } as ClientTelemetryEvent;
            telemetryDataService.reportAsync(txEvent);
        }
    }

    public render() {

        const {classes} = this.props;

        const {finishedMeet} = this.props.contextStore!;

        const ratings = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

        return <Dialog  open={this.props.open}
                       onClose={this.props.onClose}
                       disableBackdropClick= {finishedMeet}
                       aria-labelledby="form-dialog-title">
             <CloseableDialogTitle id="form-dialog-title" onClose={this.onCloseMeet}>
                 {
                    !finishedMeet ? i18n.t("feedback_title_ongoing")
                                     : i18n.t("feedback_title_finished")
                }
            </CloseableDialogTitle>
             <DialogContent className={classes!.dialogContent}>
                <DialogContentText>
                    {i18n.t("feedback_title_question")}
                </DialogContentText>
                <div className={classes!.buttonGroup} key="rating-group">
                    {ratings.map((rating) => this.ratingButton(rating))}
                </div>

                <div className={classes!.ratingLegend} key="rating-legend">
                    <Typography variant="caption">
                        { i18n.t("feedback_value_worst") }
                    </Typography>
                    <Typography variant="caption">
                        { i18n.t("feedback_value_best") }
                    </Typography>
                </div>
                {this.state.rating > -1 && this.getFeedbackTextForm()}

            </DialogContent>

            <DialogActions className={classes!.legend}>
                <Button variant="text"
                        onClick={() => window.open(`mailto:office@meet.rs?subject=${i18n.t("feedback_mail_subject")}`)}
                        style={{
                            textTransform: "lowercase",
                        }}>
                    <AppIcon icon={AppIconType.EmailOutline} className={classes!.leftSmallIcon} />
                    office@meet.rs
                </Button>

                <Button variant="contained"
                       color="primary"
                       size="large"
                       disabled={this.state.rating === -1}
                        // tslint:disable-next-line: jsx-no-lambda
                        onClick={() => this.onSend()}>
                    { i18n.t("feedback_button_text") }
               </Button>
            </DialogActions>

        </Dialog>;
    }

    private getFeedbackTextForm() {
        const {classes} = this.props;
        return <>
                <br />
                {this.state.rating >= 9 &&
                    <DialogContentText>
                        { i18n.t("feedback_cta_9_10") }
                    </DialogContentText>
                }

                {this.state.rating >= 7 && this.state.rating <= 8 &&
                    <DialogContentText>
                        { i18n.t("feedback_cta_7_8") }
                    </DialogContentText>
                }

                {this.state.rating >= 4 && this.state.rating <= 6 &&
                    <DialogContentText>
                        { i18n.t("feedback_cta_4_6") }
                    </DialogContentText>
                }

                {this.state.rating >= 0 && this.state.rating <= 3 &&
                    <DialogContentText>
                        { i18n.t("feedback_cta_0_3") }
                    </DialogContentText>
                }

                
                {this.state.rating === -1 &&
                    <DialogContentText>
                        { i18n.t("feedback_cta_7_8") }
                    </DialogContentText>
                }

                <textarea   value={this.state.text}
                            // tslint:disable-next-line: jsx-no-lambda
                            onChange={(e) => this.setState({
                                text: e.target.value,
                            })}
                            className={classes!.text}
                           autoFocus={true}
                            title={ i18n.t("feedback_text_title") }
                            rows={3} />
                 <Typography variant="caption" align="center" style={{ paddingTop: 4 }}>
                    <em>{ i18n.t("feedback_text_legend") }</em>
                </Typography>
            </>;
    }

    private onCloseMeet = () => {

        this.props.onClose();
/*
        const finishedMeeting = this.props.contextStore!.meetingState === PredefinedMeetingState.MEETING_COMPLETED;
        if (finishedMeeting) {
            window.location.href = this.props.configStore!.hosts.web;
        }
*/
    }

    private onSend = () => {
        const { environment, version } = this.props.configStore!

        const screenInfo = utils.getScreenInfo();

        const feedback: FeedbackRequest = {
            browser: utils.getBrowserName(),
            device: utils.getDeviceType(),
            isTouchEnabled: screenInfo.isTouchEnabled,
            operatingSystem: utils.getOsName(),
            rating: this.state.rating,
            screenHeight: screenInfo.height.toString(),
            screenWidth: screenInfo.width.toString(),
            text: this.state.text,
        };

        if (feedback.text) {
            const { resourceId, resourceType } = this.props.contextStore!;
            const scenario = isTeamsAddonMode   ? PredefinedAchievementScenario.TEAMS_ONBOARDING 
                                                : PredefinedAchievementScenario.MEET_GENERAL;
    
            const achievement: AchivementsRequest  = {
                scenario: scenario,
                checkpoint: PredefinedAchievementCheckpoint.MEET_FEEDBACK_TEXT,
                resourceId: resourceId!,
                resourceType: resourceType!
            };
            
            achievementsService.postAsync(achievement);
            
            const txEvent = {
                correlationId: this.correlationId,
                environment: environment,
                name: "spa_meetApp_feedbackText",
                sentryEventId: "N/A",
                silent: false,
                source: "meetApp",
                success: true,
                timeToComplete: 0,
                version: version,
                
            } as ClientTelemetryEvent;
            telemetryDataService.reportAsync(txEvent);
        }

        feedbackService.patchAsync(feedback)
            .then(() => {
                this.setState({
                    rating: -1,
                    text: undefined,
                });
            });

        this.onCloseMeet();
    }

    private ratingButton = (rating: number) => {
        const {classes} = this.props;

        const variant = this.state.rating === rating ? "contained" : "outlined";
        const color = this.state.rating === rating ? "secondary" : "default";

        let ratingClassName = classes!.button;
        if (rating === 0) {
            ratingClassName = classes!.firstButton;
        } else if (rating === 10) {
            ratingClassName = classes!.lastButton;
        }

        return <Button  className={ratingClassName}
                       key={`rating-${rating}`}
                        variant={variant}
                        color={color}
                        size="small"
                        onClick={ () => this.sendRating(rating) }>
                    {rating}
                </Button>;
    }

    private sendRating = (rating: number) => {

        const { resourceId, resourceType } = this.props.contextStore!;
        const { environment, version } = this.props.configStore!;

        this.setState({ rating });

        const scenario = isTeamsAddonMode   ? PredefinedAchievementScenario.TEAMS_ONBOARDING 
                                            : PredefinedAchievementScenario.MEET_GENERAL;

        const achievement: AchivementsRequest  = {
            scenario: scenario,
            checkpoint: PredefinedAchievementCheckpoint.MEET_FEEDBACK_RATE,
            resourceId: resourceId!,
            resourceType: resourceType!
        };
        
        achievementsService.postAsync(achievement);

        const txEvent = {
            correlationId: this.correlationId,
            environment: environment,
            name: "spa_meetApp_feedbackRating",
            sentryEventId: "N/A",
            silent: false,
            source: "meetApp",
            success: true,
            timeToComplete: 0,
            version: version,
            properties: {
                rating: rating.toString()
            }
            
        } as ClientTelemetryEvent;
        telemetryDataService.reportAsync(txEvent);

        const screenInfo = utils.getScreenInfo();
        const feedback: FeedbackRequest = {
            browser: utils.getBrowserName(),
            device: utils.getDeviceType(),
            isTouchEnabled: screenInfo.isTouchEnabled,
            operatingSystem: utils.getOsName(),
            rating,
            screenHeight: screenInfo.height.toString(),
            screenWidth: screenInfo.width.toString(),
            text: this.state.text,
        };

        return feedbackService.patchAsync(feedback);
    }
}

export default withStyles(styles, {withTheme: true})(Feedback);
